import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CaretRight, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel } from "@/components/admin/AdminUI";
import UserStatus from "@/components/admin/UserStatus";
import { formatDate } from "@/lib/format";

const STATUSES = [{ k: "all", l: "All statuses" }, { k: "active", l: "Active" }, { k: "banned", l: "Banned" }, { k: "deleted", l: "Deleted" }];

function UserRow({ u }) {
  return (
    <li>
      <Link to={`/admin/users/${u.id}`} className="flex items-center gap-4 px-5 py-4 transition-colors cw-ease hover:bg-[var(--cw-surface)]" data-testid={`user-row-${u.id}`}>
        <div className="min-w-0 flex-1">
          <p className="font-medium tabular-nums">{u.first_name || u.profile?.first_name ? <>{u.first_name || u.profile?.first_name} <span className="cw-soft font-normal">{u.phone}</span></> : u.phone}</p>
          <p className="cw-muted truncate text-xs">{[u.recovery_email, u.id].filter(Boolean).join(" · ")}</p>
        </div>
        <UserStatus user={u} />
        <span className="cw-muted hidden w-40 text-right text-sm sm:block">{formatDate(u.created_at, false)}</span>
        <CaretRight size={16} className="cw-muted" aria-hidden />
      </Link>
    </li>
  );
}

export default function Users() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const status = params.get("status") || "all";
  const qs = new URLSearchParams({ limit: "50" });
  if (params.get("q")) qs.set("q", params.get("q"));
  if (status !== "all") qs.set("status", status);
  const { data, error, loading, reload } = useAdminLoad(`/admin/users?${qs}`);

  const update = (next) => {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v && v !== "all" ? p.set(k, v) : p.delete(k)));
    setParams(p);
  };

  return (
    <div data-testid="admin-users-page">
      <PageHeader title="Users" description="Search by first name, phone, recovery email or user ID. Every lookup is recorded in the Admin Log." />
      <form onSubmit={(e) => { e.preventDefault(); update({ q: q.trim() }); }} className="mb-6 flex flex-col gap-3 sm:flex-row" role="search">
        <label htmlFor="user-search" className="sr-only">Name, phone, email or user ID</label>
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="cw-muted absolute left-4 top-1/2 -translate-y-1/2" aria-hidden />
          <Input id="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, +1 555 123 0001, email or user ID" className="h-11 rounded-xl bg-white pl-11" data-testid="user-search-input" />
        </div>
        <Select value={status} onValueChange={(v) => update({ status: v })}>
          <SelectTrigger className="h-11 rounded-xl bg-white sm:w-44" aria-label="Status" data-testid="user-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s.k} value={s.k}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="submit" className="h-11 rounded-xl px-6" data-testid="user-search-button">Search</Button>
      </form>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <Panel className="overflow-hidden">
          <p className="cw-muted border-b border-border px-5 py-3 text-xs" data-testid="user-results-count">{data?.total ?? 0} result{data?.total === 1 ? "" : "s"}</p>
          {data?.items?.length ? <ul className="divide-y divide-border">{data.items.map((u) => <UserRow key={u.id} u={u} />)}</ul>
            : <Empty title="No users found" text="Check the number or ID and try again." testId="users-empty" />}
        </Panel>
      </LoadState>
    </div>
  );
}
